/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Personalization API", description = "A fast, flexible API to tailor customer experiences across web, mobile, and email.", lastUpdatedDate = new Date(1744588800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Personalization API",
        description: "A fast, flexible API to tailor customer experiences across web, mobile, and email.",
        lastUpdatedDate: "Mon Apr 14 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "hightouch-personalization-api",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Architecture",
          "depth": 0,
          "id": "architecture"
        }, {
          "value": "Implementation steps",
          "depth": 0,
          "id": "implementation-steps"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Delete behavior",
          "depth": 1,
          "id": "delete-behavior"
        }, {
          "value": "Access data",
          "depth": 0,
          "id": "access-data"
        }, {
          "value": "Create an API key",
          "depth": 1,
          "id": "create-an-api-key"
        }, {
          "value": "Make API requests",
          "depth": 1,
          "id": "make-api-requests"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Unique primary key requirement",
          "depth": 1,
          "id": "unique-primary-key-requirement"
        }, {
          "value": "Recommended usage",
          "depth": 1,
          "id": "recommended-usage"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }, {
          "value": "Merging records",
          "depth": 1,
          "id": "merging-records"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      ul: "ul",
      li: "li",
      strong: "strong",
      img: "img",
      ol: "ol",
      code: "code",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      em: "em",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PylonLink, CreateAPIKeySnippet, LiveDebugger, SyncAlerts} = _components;
    if (!CreateAPIKeySnippet) _missingMdxReference("CreateAPIKeySnippet", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This feature is available as an add-on to ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier plans"
          }), ". To get access and learn more about implementation, please ", _jsx(PylonLink, {
            pylonText: "Hello, I'm interested in getting access to the Personalization API",
            linkText: "get in touch"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch Personalization API is a fully managed service that combines the analytical power of the data warehouse with the real-time performance of a low-latency API. You can think of this feature as a cache for your data warehouse."
      }), "\n", _jsx(_components.p, {
        children: "With the Personalization API, you can serve any data model—customers, products, transactions, recommendations, etc.—to any internal app, web experience, or email marketing platform. Define your dataset with SQL and Hightouch handles the rest."
      }), "\n", _jsx(_components.p, {
        children: "Example use cases include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Powering personalized marketing campaigns"
          }), " by enriching marketing touchpoints with dynamic product recommendations"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delivering in-app or web personalization"
          }), " like customized search results, cohort-based pricing and messaging, article recommendations, or nearest store locations"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Optimizing product experimentation"
          }), " by delivering up-to-date customer information like audience inclusion/exclusion to decisioning and experimentation systems"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "architecture",
        children: "Architecture"
      }), "\n", _jsxs(_components.p, {
        children: ["Under the hood, the Personalization API is backed by a distributed, low-latency database that stores a cache of your model. This cache updates as frequently as you see fit with Hightouch's ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui",
          children: "scheduling options"
        }), ". Hightouch then serves this cache over HTTP so that it can be queried by your SaaS tools and other services."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-personalization-api-diagram.png",
          alt: "Personalization API diagram"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Our benchmarks show p99 response times under 30 milliseconds in the same region. The Personalization API can support peak throughput of 1 million requests per second. To obtain the most recent benchmarks for your region, please ", _jsx(PylonLink, {
          pylonText: "Hello, I would like Personalization API benchmarks for the ___ region.",
          linkText: "contact us"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "implementation-steps",
        children: "Implementation steps"
      }), "\n", _jsx(_components.p, {
        children: "Using this feature is a three-step process:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Enabling the Personalization API in the regions closest to your own infrastructure"
        }), "\n", _jsx(_components.li, {
          children: "Creating syncs to define which models should be materialized in the Hightouch-managed cache"
        }), "\n", _jsx(_components.li, {
          children: "Using your HTTP endpoints to access data"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "After the Personalization API is enabled for your workspace, navigate to the newly created destination and configure the region from which the API should be served."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-personalization-api-destination-config.png",
          alt: "Personalization API destination configuration"
        })
      }), "\n", _jsx(_components.p, {
        children: "Currently, the following AWS regions are available for self-serve deployment:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "us-east-1"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "us-west-2"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "eu-west-1"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For other regions, please ", _jsx(PylonLink, {
          pylonText: "Hello, I would like to request a new region for the Personalization API.",
          linkText: "contact us"
        }), " to complete setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.p, {
        children: "There are two core concepts in the Personalization API: collections and records."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Collections"
          }), " are conceptually similar to database tables. Each should represent a particular type of data, such as customers or invoices. Collection names must be alphanumeric and will become part of your Personalization API endpoint."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Records"
          }), " are conceptually similar to rows in a database. Each should represent a single item, like a customer profile. Collections consist of one or more records."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can create multiple syncs to the same collection provided that each sync updates different record fields."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you might have one model representing basic customer demographic data—", _jsx(_components.code, {
          children: "age"
        }), ", ", _jsx(_components.code, {
          children: "gender"
        }), ", ", _jsx(_components.code, {
          children: "location"
        }), ", etc.—and another model representing account usage data—", _jsx(_components.code, {
          children: "last_login_at"
        }), ", ", _jsx(_components.code, {
          children: "num_messages_sent"
        }), ", etc. By creating two syncs to the same ", _jsx(_components.code, {
          children: "customers"
        }), " collection, you can create more complex records that contain all attributes necessary for personalization."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-personalization-api-collection.png",
          alt: "Personalization API collection name"
        })
      }), "\n", _jsx(_components.p, {
        children: "Next, specify which column from your model should serve as the primary index for record lookups. This field must uniquely identify each record in the collection and is often the same as your model's primary key."
      }), "\n", _jsxs(_components.p, {
        children: ["The Personalization API supports lookups on multiple indices. For example, you might want to retrieve customer profiles using ", _jsx(_components.code, {
          children: "user_id"
        }), ", ", _jsx(_components.code, {
          children: "anonymous_id"
        }), ", or ", _jsx(_components.code, {
          children: "email_address"
        }), ". To enable multiple indices, please ", _jsx(PylonLink, {
          pylonText: "Hello, I would like to utilize the Personalization API with lookups on multiple indices.",
          linkText: "contact us"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-personalization-api-id.png",
          alt: "Personalization API ID mapping"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Use the field mapper to specify which columns from your model should be included in the API response payload. You can rename these fields and use the advanced mapper to apply transformations using the ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/",
          children: "Liquid template language"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-personalization-api-field-mapper.png",
          alt: "Personalization API field mapper"
        })
      }), "\n", _jsx(_components.h3, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "You have the following options for how Hightouch should handle rows leaving your query results:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsxs(_components.td, {
              children: ["All cached data is preserved indefinitely. This option is ", _jsx(_components.em, {
                children: "strongly"
              }), " recommended against."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear the fields managed by this sync but do not delete the entire record"
              })
            }), _jsx(_components.td, {
              children: "Fields managed by other syncs are preserved."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete the entire record"
              })
            }), _jsxs(_components.td, {
              children: ["Requests for deleted records receive ", _jsx(_components.code, {
                children: "404 Not Found"
              }), " responses."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "You probably want to delete the entire record if your collection is managed using only one sync. However, if multiple syncs are writing to the same collection, it's best to clear only the fields updated by this sync."
      }), "\n", _jsx(_components.h2, {
        id: "access-data",
        children: "Access data"
      }), "\n", _jsx(_components.p, {
        children: "To start using the API, you must first create an API key for your workspace."
      }), "\n", _jsx(_components.h3, {
        id: "create-an-api-key",
        children: "Create an API key"
      }), "\n", _jsx(CreateAPIKeySnippet, {}), "\n", _jsx(_components.h3, {
        id: "make-api-requests",
        children: "Make API requests"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you have an API key, you can make requests to the Personalization API to retrieve data. The API is accessible at ", _jsx(_components.code, {
          children: "https://personalization.{region}.hightouch.com"
        }), ", for example, ", _jsx(_components.code, {
          children: "https://personalization.us-west-2.hightouch.com"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "authentication",
        children: "Authentication"
      }), "\n", _jsx(_components.p, {
        children: "You can specify your API key via a bearer token in an HTTP Authorization header."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-makefile",
          children: [_jsx(_components.span, {
            className: "hljs-section",
            children: "Authorization: Bearer XXXXX"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "usage",
        children: "Usage"
      }), "\n", _jsx(_components.p, {
        children: "The API has a single endpoint:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Verb"
            }), _jsx(_components.th, {
              children: "Path"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "GET"
            }), _jsxs(_components.td, {
              children: ["/v1/collections/", _jsx(_components.code, {
                children: ":collection_name"
              }), "/records/id/", _jsx(_components.code, {
                children: ":index_value"
              })]
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to your ", _jsx(_components.a, {
          href: "#sync-configuration",
          children: "sync configuration"
        }), " for your ", _jsx(_components.code, {
          children: "collection_name"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "A request looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["curl -X GET \\\n     -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Authorization: Bearer 55555555-4444-3333-2222-111111111111\""
          }), " \\\n     ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"https://personalization.us-west-2.hightouch.com/v1/collections/customers/records/id/12345\""
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When the query is successful, the HTTP response is a ", _jsx(_components.code, {
          children: "200 OK"
        }), " and returns the record specified by the index value, in this case the customer with ", _jsx(_components.code, {
          children: "user_id"
        }), " (the index key) equal to ", _jsx(_components.code, {
          children: "12345"
        }), " (the index value). The data in the response payload is entirely flexible and defined by your ", _jsx(_components.a, {
          href: "#sync-configuration",
          children: "sync configuration"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"user_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "12345"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"full_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Jane Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"lifetime_value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1492.18"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"churn_risk\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "0.04"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"90_day_summary\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"num_songs_listened\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "813"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"top_genres\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"house\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"techno\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ambient\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"top_artists\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"deadmau5\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Marsh\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Enamour\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"recommendations\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"concerts\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n                ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"artist\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Aphex Twin\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n                ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"location\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco, CA\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n                ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"event_date\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-01-31\""
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n                ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"artist\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Sultan + Shepard\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n                ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"location\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco, CA\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n                ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"event_date\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-02-25\""
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"upcoming_album_release\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"title\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Universal Language\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"artist\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Simon Doty\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"label\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Anjunadeep\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"release_date\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-04-28\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"_audiences\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"top_concert_ticket_purchasers\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "true"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"attended_concert_last_month\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "false"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"electronic_music_listeners\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "true"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "unique-primary-key-requirement",
        children: "Unique primary key requirement"
      }), "\n", _jsxs(_components.p, {
        children: ["The model you select for your sync configuration ", _jsx(_components.em, {
          children: "must"
        }), " have a unique identifier as its ", _jsx(_components.a, {
          href: "/getting-started/concepts#unique-primary-key-requirement",
          children: "primary key"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "recommended-usage",
        children: "Recommended usage"
      }), "\n", _jsx(_components.p, {
        children: "The Personalization API should not be called directly from a webpage, mobile app, or any untrusted client. We recommend proxying API calls through a service that you own, such as your application backend. This allows you to enforce reasonable rate limits and ensure that clients cannot retrieve data belonging to other users. Keep in mind that authentication is performed using a Hightouch API key, which grants access to all records in your Personalization API collection."
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {}), "\n", _jsx(_components.h3, {
        id: "merging-records",
        children: "Merging records"
      }), "\n", _jsxs(_components.p, {
        children: ["When ", _jsx(_components.a, {
          href: "#sync-configuration",
          children: "configuring multiple syncs to update the same collection"
        }), ",\nthere are some common errors and misconfigurations to be aware of.\nIn general, it is simpler and less error-prone to ensure that different syncs do not attempt to manage\nthe same fields."]
      }), "\n", _jsx(_components.h4, {
        id: "merging-records-with-the-same-field-names",
        children: "Merging records with the same field names"
      }), "\n", _jsxs(_components.p, {
        children: ["If two syncs both update the ", _jsx(_components.em, {
          children: "same field"
        }), " on the record, it will of course take whatever value is written last.\nThis may have unexpected results if a record is ", _jsx(_components.em, {
          children: "removed"
        }), " from one sync, but not another, as the behavior\nis defined by whatever ", _jsx(_components.a, {
          href: "#delete-behavior",
          children: "deletion behavior"
        }), " is specified for the sync that deletes the record.\nFor example, the record may be deleted, or if two syncs write to the same field name, the field may be cleared\nand thus have no value at all until another sync updates the record."]
      }), "\n", _jsx(_components.h4, {
        id: "merging-records-with-nested-fields",
        children: "Merging records with nested fields"
      }), "\n", _jsxs(_components.p, {
        children: ["When merging records, e.g. writing an ", _jsx(_components.code, {
          children: "address"
        }), " field to a ", _jsx(_components.code, {
          children: "customer"
        }), " record created by another sync, the\nmerging operates simply by replacing the ", _jsx(_components.code, {
          children: "address"
        }), " value with the new one. There is no special logic to\nmerge property ", _jsx(_components.em, {
          children: "values"
        }), ", or nested values. For example, if one sync creates a customer record such as\n", _jsx(_components.code, {
          children: "{ id: 1, name: \"John Doe\", address: { country: \"US\" } }"
        }), ", and another sync updates it with\n", _jsx(_components.code, {
          children: "{ id: 1, age: 33, address: { zip: 86023 } }"
        }), ",\nthe resulting record will be ", _jsx(_components.code, {
          children: "{ id: 1, age: 33, name: \"John Doe\", address: { zip: 86023 } }"
        }), ": it merges fields\nfrom both syncs (", _jsx(_components.code, {
          children: "name"
        }), ", ", _jsx(_components.code, {
          children: "age"
        }), "), but the value of the ", _jsx(_components.code, {
          children: "address"
        }), " field, originally ", _jsx(_components.code, {
          children: "{ country: \"US\" }"
        }), ", was\noverwritten with the new value ", _jsx(_components.code, {
          children: "{ zip: 86023 }"
        }), ". It is not possible in the Personalization API to perform\na deep merge, setting the field to ", _jsx(_components.code, {
          children: "{ country: \"US\", zip: 86023 }"
        }), ". Instead, we recommend setting the\nentire ", _jsx(_components.code, {
          children: "address"
        }), " field using a single sync."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
